import { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Plus, Edit, Trash2, ArrowLeft, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';

interface CarBrand {
  id: number;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CarModel {
  id: number;
  name: string;
  brandId: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  brand: CarBrand;
}

export default function CarModelsSettings() {
  const { t } = useLanguage();
  const [models, setModels] = useState<CarModel[]>([]);
  const [brands, setBrands] = useState<CarBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [addBrandId, setAddBrandId] = useState(0);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [editModel, setEditModel] = useState<CarModel | null>(null);
  const [editName, setEditName] = useState('');
  const [editBrandId, setEditBrandId] = useState(0);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteDeleting, setDeleteDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBrandId, setFilterBrandId] = useState<number | ''>('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [modelsData, brandsData] = await Promise.all([
        api.get('/api/models'),
        api.get('/api/brands')
      ]);
      setModels(Array.isArray(modelsData) ? modelsData : []);
      setBrands(Array.isArray(brandsData) ? brandsData : []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!addName.trim() || !addBrandId) return;
    setAddSubmitting(true);
    try {
      await api.post('/api/models', {
        name: addName.trim(),
        brandId: addBrandId,
        active: true
      });
      setAddName('');
      setAddBrandId(0);
      setAddOpen(false);
      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setAddSubmitting(false);
    }
  };

  const openEdit = (model: CarModel) => {
    setEditModel(model);
    setEditName(model.name);
    setEditBrandId(model.brandId);
  };

  const handleUpdate = async () => {
    if (editModel == null || !editName.trim() || !editBrandId) return;
    setEditSubmitting(true);
    try {
      await api.put(`/api/models/${editModel.id}`, {
        name: editName.trim(),
        brandId: editBrandId,
        active: editModel.active
      });
      setEditModel(null);
      setEditName('');
      setEditBrandId(0);
      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    setDeleteDeleting(true);
    try {
      await api.delete(`/api/models/${deleteId}`);
      setDeleteId(null);
      fetchData();
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteDeleting(false);
    }
  };

  const modelToDelete = deleteId != null ? models.find((m) => m.id === deleteId) : null;
  const filteredModels = models.filter((m) => {
    const matchesSearch = m.name.toLowerCase().includes(searchQuery.trim().toLowerCase()) ||
      m.brand.name.toLowerCase().includes(searchQuery.trim().toLowerCase());
    const matchesBrand = filterBrandId === '' || m.brandId === filterBrandId;
    return matchesSearch && matchesBrand;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to="/settings">
              <Button variant="outline" size="sm" className="border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg text-sm font-medium">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('settings.back')}
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 tracking-tight">{t('settings.modelsManagement')}</h1>
              <p className="text-slate-500 text-sm mt-0.5">{t('settings.modelsDescription')}</p>
            </div>
          </div>
          <Button onClick={() => setAddOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="h-4 w-4 mr-2" />
            {t('settings.addModel')}
          </Button>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardContent className="p-6">
            {models.length > 0 && (
              <div className="flex flex-wrap gap-3 mb-4">
                <div className="relative max-w-xs flex-1 min-w-[180px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder={t('settings.search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 rounded-lg border-slate-200"
                  />
                </div>
                <Select value={filterBrandId === '' ? 'all' : String(filterBrandId)} onValueChange={(v) => setFilterBrandId(v === 'all' ? '' : Number(v))}>
                  <SelectTrigger className="w-[200px] rounded-lg border-slate-200">
                    <SelectValue placeholder={t('settings.filterByBrand')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('settings.filterByBrand')} (all)</SelectItem>
                    {brands.map((brand) => (
                      <SelectItem key={brand.id} value={String(brand.id)}>{brand.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-10 w-full rounded-lg" />
                ))}
              </div>
            ) : models.length === 0 ? (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-12 text-center">
                <p className="text-slate-500">{t('settings.noModelsYet')}</p>
              </div>
            ) : filteredModels.length === 0 ? (
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-12 text-center">
                <p className="text-slate-500">{t('settings.search')} — {t('settings.noSearchResults')}</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('settings.modelName')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('settings.brand')}</TableHead>
                    <TableHead className="text-slate-600 font-medium text-sm">{t('settings.created')}</TableHead>
                    <TableHead className="w-[120px] text-right text-slate-600 font-medium text-sm">{t('settings.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredModels.map((model) => (
                    <TableRow key={model.id} className="hover:bg-slate-50">
                      <TableCell className="text-sm text-slate-700 font-medium">{model.name}</TableCell>
                      <TableCell className="text-sm text-slate-700">{model.brand.name}</TableCell>
                      <TableCell className="text-sm text-slate-500 tabular-nums">
                        {new Date(model.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
                            onClick={() => openEdit(model)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => setDeleteId(model.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Model Dialog */}
      <Dialog open={addOpen} onOpenChange={(open) => !addSubmitting && setAddOpen(open)}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('settings.addModel')}</DialogTitle>
            <DialogDescription>{t('settings.modelsDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.modelName')}</Label>
              <Input
                placeholder={t('settings.modelName')}
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                className="rounded-lg border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.brand')}</Label>
              <Select
                value={addBrandId ? String(addBrandId) : ''}
                onValueChange={(v) => setAddBrandId(v ? parseInt(v, 10) : 0)}
              >
                <SelectTrigger className="w-full rounded-lg border-slate-200">
                  <SelectValue placeholder={t('settings.selectBrand')} />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={String(brand.id)}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setAddOpen(false)} disabled={addSubmitting} className="border-slate-200">
              {t('settings.cancel')}
            </Button>
            <Button type="button" onClick={handleCreate} disabled={!addName.trim() || !addBrandId || addSubmitting} className="bg-slate-900 hover:bg-slate-800">
              {t('settings.addModel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Model Dialog */}
      <Dialog open={editModel != null} onOpenChange={(open) => !open && !editSubmitting && setEditModel(null)}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('settings.editModel')}</DialogTitle>
            <DialogDescription>{t('settings.modelsDescription')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.modelName')}</Label>
              <Input
                placeholder={t('settings.modelName')}
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="rounded-lg border-slate-200"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-700">{t('settings.brand')}</Label>
              <Select
                value={editBrandId ? String(editBrandId) : ''}
                onValueChange={(v) => setEditBrandId(v ? parseInt(v, 10) : 0)}
              >
                <SelectTrigger className="w-full rounded-lg border-slate-200">
                  <SelectValue placeholder={t('settings.selectBrand')} />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((brand) => (
                    <SelectItem key={brand.id} value={String(brand.id)}>
                      {brand.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setEditModel(null)} disabled={editSubmitting} className="border-slate-200">
              {t('settings.cancel')}
            </Button>
            <Button type="button" onClick={handleUpdate} disabled={!editName.trim() || !editBrandId || editSubmitting} className="bg-slate-900 hover:bg-slate-800">
              {t('settings.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Model Dialog */}
      <Dialog open={deleteId != null} onOpenChange={(open) => !open && !deleteDeleting && setDeleteId(null)}>
        <DialogContent className="sm:max-w-md border-slate-200">
          <DialogHeader>
            <DialogTitle>{t('settings.deleteModel')}</DialogTitle>
            <DialogDescription>
              {modelToDelete ? `"${modelToDelete.name}" ${t('settings.confirmDelete')}` : t('settings.confirmDelete')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => setDeleteId(null)} disabled={deleteDeleting} className="border-slate-200">
              {t('settings.cancel')}
            </Button>
            <Button type="button" variant="destructive" disabled={deleteDeleting} className="bg-red-600 hover:bg-red-700" onClick={handleDelete}>
              {deleteDeleting ? t('settings.deleting') : t('settings.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
